import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Button, Fade, keyframes } from '@mui/material';
import { useOnboarding, ONBOARDING_STEPS } from '../../context/OnboardingContext';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.5); }
  70% { box-shadow: 0 0 0 14px rgba(102, 126, 234, 0); }
  100% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0); }
`;

/**
 * SpotlightGuide — renders a pulsing highlight ring around a target element
 * and a floating tooltip with instructions. Repositions on scroll/resize.
 */
export default function SpotlightGuide() {
  const { activeGuide, stopGuide, skipAll, isStepComplete } = useOnboarding();
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const pollRef = useRef(null);
  const rafRef = useRef(null);

  const step = ONBOARDING_STEPS.find(s => s.id === activeGuide);

  // Poll for the target element (may be lazy-loaded)
  useEffect(() => {
    if (!step?.target) {
      setTargetRect(null);
      setVisible(false);
      return;
    }

    // Already completed — don't show
    if (isStepComplete(step.id)) {
      setTargetRect(null);
      setVisible(false);
      return;
    }

    let attempts = 0;
    const maxAttempts = 50; // 5 seconds

    const findTarget = () => {
      const el = document.querySelector(step.target);
      if (el) {
        clearInterval(pollRef.current);
        updatePosition(el);
        setVisible(true);

        // Scroll the element into view if needed
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else if (++attempts >= maxAttempts) {
        clearInterval(pollRef.current);
      }
    };

    pollRef.current = setInterval(findTarget, 100);
    findTarget(); // try immediately

    return () => {
      clearInterval(pollRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [step, isStepComplete]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!visible || !step?.target) return;

    const reposition = () => {
      const el = document.querySelector(step.target);
      if (el) {
        updatePosition(el);
      } else {
        setVisible(false);
      }
    };

    const throttledReposition = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(reposition);
    };

    window.addEventListener('scroll', throttledReposition, true);
    window.addEventListener('resize', throttledReposition);
    // Also reposition periodically in case layout shifts
    const interval = setInterval(reposition, 1000);

    return () => {
      window.removeEventListener('scroll', throttledReposition, true);
      window.removeEventListener('resize', throttledReposition);
      clearInterval(interval);
      cancelAnimationFrame(rafRef.current);
    };
  }, [visible, step]);

  const updatePosition = (el) => {
    const rect = el.getBoundingClientRect();
    setTargetRect({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  };

  if (!activeGuide || !step || !targetRect || !visible) return null;

  // Calculate tooltip position — prefer below, shift left if near right edge
  const padding = 8;
  const tooltipTop = targetRect.top + targetRect.height + padding + 12;
  const tooltipBottom = tooltipTop > window.innerHeight - 200;
  const finalTop = tooltipBottom
    ? targetRect.top - padding - 12 // above
    : tooltipTop;
  const tooltipLeft = Math.min(
    Math.max(16, targetRect.left),
    window.innerWidth - 320
  );

  return (
    <>
      {/* Semi-transparent overlay (click-through except on tooltip) */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          pointerEvents: 'none',
        }}
      />

      {/* Pulsing ring around the target */}
      <Fade in>
        <Box
          sx={{
            position: 'fixed',
            top: targetRect.top - padding,
            left: targetRect.left - padding,
            width: targetRect.width + padding * 2,
            height: targetRect.height + padding * 2,
            borderRadius: 2,
            border: '2px solid #667eea',
            animation: `${pulse} 2s infinite`,
            zIndex: 1301,
            pointerEvents: 'none',
          }}
        />
      </Fade>

      {/* Instruction tooltip */}
      <Fade in>
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            top: tooltipBottom ? undefined : finalTop,
            bottom: tooltipBottom ? window.innerHeight - targetRect.top + padding + 12 : undefined,
            left: tooltipLeft,
            zIndex: 1302,
            maxWidth: 300,
            borderRadius: 2,
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          {/* Arrow pointing to target */}
          <Box
            sx={{
              position: 'absolute',
              [tooltipBottom ? 'bottom' : 'top']: -6,
              left: Math.max(20, targetRect.left + targetRect.width / 2 - tooltipLeft - 6),
              width: 12,
              height: 12,
              bgcolor: '#667eea',
              transform: 'rotate(45deg)',
              zIndex: -1,
            }}
          />

          {/* Header */}
          <Box sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            px: 2, py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
              Step {ONBOARDING_STEPS.findIndex(s => s.id === step.id) + 1} of {ONBOARDING_STEPS.length}
            </Typography>
          </Box>

          {/* Body */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
              {step.label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {step.hint}
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{
            px: 2, py: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <Button
              size="small"
              onClick={skipAll}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem' }}
            >
              Skip setup
            </Button>
            <Button
              size="small"
              onClick={stopGuide}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              I'll do this later
            </Button>
          </Box>
        </Paper>
      </Fade>
    </>
  );
}
